import React, { useState, useEffect, useCallback } from 'react';
import { Link, RefreshCw, Info, Loader2, ExternalLink } from 'lucide-react';
import { GlassCard, GlassButton } from '../ui';
import { suggestInternalLinks, applyInternalLinks, InternalLinkSuggestion } from '../../services/api';
import './InternalLinksPanel.css';

interface InternalLinksPanelProps {
    taskId: string | undefined;
    onApplied?: () => void;
}

const InternalLinksPanel: React.FC<InternalLinksPanelProps> = ({ taskId, onApplied }) => {
    const [suggestions, setSuggestions] = useState<InternalLinkSuggestion[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [rejected, setRejected] = useState<Set<string>>(new Set());
    const [applying, setApplying] = useState<string | null>(null);

    const fetchSuggestions = useCallback(async () => {
        if (!taskId) return;
        setLoading(true);
        setError(null);
        try {
            const result = await suggestInternalLinks(taskId);
            setSuggestions(result.suggestions);
            setRejected(new Set());
        } catch (err: any) {
            const detail = err?.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : 'Failed to fetch suggestions');
        } finally {
            setLoading(false);
        }
    }, [taskId]);

    useEffect(() => {
        fetchSuggestions();
    }, [fetchSuggestions]);

    const handleAccept = async (suggestion: InternalLinkSuggestion) => {
        if (!taskId) return;
        setApplying(suggestion.target_task_id);
        try {
            await applyInternalLinks(taskId, [suggestion], 'suggested_accepted');
            setSuggestions(prev => prev.filter(s => s.target_task_id !== suggestion.target_task_id));
            onApplied?.();
        } catch (err: any) {
            const detail = err?.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : 'Failed to apply suggestion');
        } finally {
            setApplying(null);
        }
    };

    const handleReject = (targetTaskId: string) => {
        setRejected(prev => {
            const next = new Set(prev);
            next.add(targetTaskId);
            return next;
        });
    };

    const visibleSuggestions = suggestions.filter(s => !rejected.has(s.target_task_id));

    return (
        <GlassCard variant="default" padding="md" className="internal-links-panel">
            <div className="ilp-header">
                <div className="ilp-title">
                    <Link size={16} />
                    <span>Internal Links</span>
                </div>
                <button
                    className="ilp-refresh"
                    onClick={fetchSuggestions}
                    disabled={loading}
                    title="Refresh suggestions"
                >
                    <RefreshCw size={14} className={loading ? 'spinning' : ''} />
                </button>
            </div>

            {loading && (
                <div className="ilp-loading">
                    <Loader2 size={20} className="spinning" />
                    <span>Finding suggestions…</span>
                </div>
            )}

            {error && !loading && (
                <div className="ilp-error">
                    <Info size={14} />
                    <span>{error}</span>
                </div>
            )}

            {!loading && !error && visibleSuggestions.length === 0 && (
                <div className="ilp-empty">
                    <Info size={16} />
                    <p>No suggestions found for this draft.</p>
                    <p className="ilp-empty-hint">
                        Index more articles to unlock suggestions (need ≥10 peers).
                    </p>
                </div>
            )}

            {!loading && visibleSuggestions.length > 0 && (
                <div className="ilp-suggestions">
                    {visibleSuggestions.map(suggestion => (
                        <div key={suggestion.target_task_id} className="ilp-suggestion-row">
                            <div className="ilp-suggestion-header">
                                <a
                                    href={`/preview/${suggestion.target_task_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="ilp-target-title"
                                >
                                    {suggestion.target_title}
                                    <ExternalLink size={11} />
                                </a>
                                <span className="ilp-confidence-pill">
                                    {Math.round(suggestion.confidence * 100)}%
                                </span>
                            </div>
                            <div className="ilp-anchor-text">
                                <code>{suggestion.anchor_text}</code>
                            </div>
                            <div className="ilp-sentence-context">
                                <span className="ilp-sentence-label">Context:</span>
                                <span className="ilp-sentence-text">
                                    {suggestion.sentence_context.length > 120
                                        ? suggestion.sentence_context.slice(0, 120) + '…'
                                        : suggestion.sentence_context}
                                </span>
                            </div>
                            <div className="ilp-actions">
                                <GlassButton
                                    size="sm"
                                    variant="primary"
                                    pill
                                    disabled={applying === suggestion.target_task_id}
                                    loading={applying === suggestion.target_task_id}
                                    onClick={() => handleAccept(suggestion)}
                                >
                                    Accept
                                </GlassButton>
                                <GlassButton
                                    size="sm"
                                    variant="secondary"
                                    disabled={applying === suggestion.target_task_id}
                                    onClick={() => handleReject(suggestion.target_task_id)}
                                >
                                    Reject
                                </GlassButton>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </GlassCard>
    );
};

export default InternalLinksPanel;
